import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import {
  useRouteLoaderData,
  useLoaderData,
  useRevalidator,
  useParams,
  useNavigate,
} from 'react-router-dom'
import Stack from '@mui/material/Stack'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import { TimeField } from '@mui/x-date-pickers-pro'
import { useTranslation } from 'react-i18next'
import { isSameDay } from 'date-fns/isSameDay'
import { toast } from 'react-toastify'
import { format } from 'date-fns/format'
import { useForm, Controller } from 'react-hook-form'

import { Sessions, Abstracts, LoaderData } from '../../../types'
import {
  formatHumanDateTime,
  formatHumanTime,
  useTagsDataOptions,
  useThemesDataOptions,
  useThemeCategoryDataOptions,
  useDomainsDataOptions,
  useAbstractSelectionStatusOptions,
  searchString,
} from '../../../utils'
import {
  useModifySelectionAbstractMutation,
  useOrderDownSelectionAbstractMutation,
  useOrderUpSelectionAbstractMutation,
} from '../../../services/events'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import TransferList from '../../root/components/TransferList'
import AbstractSelectionStatusTag from '../../abstracts/components/AbstractSelectionStatusTag'
import { adminEventDetailsLoader, adminSessionAbstractsLoader } from '../Admin.loader'
import { useContextShowStatistics } from '../Admin.Context'
import AdminAbstractsDetails from './components/AdminAbstractsDetails'
import ViewButtonGroup from './components/ViewButtonGroup'

export default function AdminSessionAbstracts() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const { eventId } = useParams()
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')
  const revalidating = useRef(false)

  const showStatistics = useContextShowStatistics()

  const { event, sessions } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >
  const { abstracts, statistics } = useLoaderData() as LoaderData<
    ReturnType<typeof adminSessionAbstractsLoader>
  >

  const { control, reset, getValues, setValue } = useForm({
    defaultValues: { abstracts },
  })

  useMemo(() => {
    reset({ abstracts })
  }, [abstracts, reset])

  const dataAbstracts = getValues('abstracts')

  const tagsDataOptions = useTagsDataOptions()
  const themesDataOptions = useThemesDataOptions()
  const themeCategoryOptions = useThemeCategoryDataOptions(t)
  const domainsDataOptions = useDomainsDataOptions()
  const selectionStatusDataOptions = useAbstractSelectionStatusOptions(t)

  const [excludeData, setExcludeData] = useState<readonly number[]>(
    dataAbstracts.reduce((acc, abstract) => {
      if (abstract.session !== null && abstract.session !== undefined) {
        acc.push(abstract.id)
      }
      return acc
    }, [] as number[])
  )
  const [selectedSession, setSelectedSession] = useState<Sessions | null>(sessions?.[0] ?? null)
  const [searchFilter, setSearchFilter] = useState<string | null>(null)
  const filterData = useCallback<(abstract: Abstracts<true>) => boolean>(
    abstract => {
      if (!searchFilter) return true
      const domain = domainsDataOptions.find(el => el.value === abstract.domain)?.label ?? ''
      const theme = themesDataOptions.find(el => el.value === abstract.theme)?.label ?? ''
      const selectionStatus =
        selectionStatusDataOptions.find(el => el.value === abstract.selection_status)?.label ?? ''
      const category = themeCategoryOptions.find(el => el.value === abstract.category)?.label ?? ''

      if (searchFilter.includes(',') || searchFilter.includes(':')) {
        const filters = searchFilter.split(',').map(el => el.trim())

        const booleanArr = filters.map(filter => {
          const type = filter.split(':')[0].trim()
          const value = filter.split(':')[1]?.trim() ?? ''

          if (!value) return undefined

          switch (type) {
            case 'domain':
              return searchString(domain, value)
            case 'theme':
              return searchString(theme, value)
            case 'category':
              return searchString(category, value)
            case 'selection_status':
              return searchString(selectionStatus, value)
            case 'id':
              return searchString(abstract.id.toString(), value)
            case 'title':
              return searchString(abstract.title, value)
            default:
              return false
          }
        })

        return booleanArr.filter(el => el !== undefined).every(el => el)
      }

      return (
        searchString(abstract.title, searchFilter) ||
        searchString(abstract.id.toString(), searchFilter) ||
        searchString(category, searchFilter) ||
        searchString(domain, searchFilter) ||
        searchString(theme, searchFilter) ||
        searchString(selectionStatus, searchFilter)
      )
    },
    [
      domainsDataOptions,
      searchFilter,
      selectionStatusDataOptions,
      themeCategoryOptions,
      themesDataOptions,
    ]
  )

  const [modifySelectionAbstract] = useModifySelectionAbstractMutation()
  const [orderUpSelectionAbstract] = useOrderUpSelectionAbstractMutation()
  const [orderDownSelectionAbstract] = useOrderDownSelectionAbstractMutation()

  const selected: readonly number[] = useMemo(
    () =>
      dataAbstracts.reduce((acc, abstract) => {
        if (abstract.session === selectedSession?.id) {
          acc.push(abstract.id)
        }
        return acc
      }, [] as number[]),
    [selectedSession, dataAbstracts]
  )

  const waitForRevalidation = () => {
    return new Promise<void>(resolve => {
      const checkState = () => {
        if (!revalidating.current) {
          resolve()
        } else {
          setTimeout(checkState, 100)
        }
      }

      setTimeout(checkState, 100)
    })
  }

  useEffect(
    function resetRevalidatingState() {
      if (revalidating.current && revalidator.state === 'idle') {
        revalidating.current = false
      }
    },
    [revalidator.state]
  )

  const defaultAbstractPresentationTime =
    selectedSession?.default_abstract_presentation_time ?? '00:15:00'
  const defaultAbstractQaTime = selectedSession?.default_abstract_qa_time ?? '00:00:00'

  const getIndexFromId = (id: number) => {
    return dataAbstracts.findIndex(abstract => abstract.id === id)
  }

  return (
    <Stack
      sx={theme => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.boxShadows.main,
      })}
      height="inherit"
      overflow="hidden auto"
      padding="0.5rem"
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">{t('Détails des résumés')}</Typography>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && <AdminAbstractsDetails statistics={statistics} />}

      <Divider />
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ margin: '1rem 0' }}
          gap="0.5rem"
        >
          <Stack direction="row" alignItems="center" gap="0.5rem">
            <h3>{t('Construction programme résumés')} :</h3>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate(`/admin/details/${eventId}/abstracts`)
              }}
            >
              {t('Liste')}
            </Button>
          </Stack>
          <ViewButtonGroup />
        </Stack>
        <Stack>
          {selectedSession ? (
            <>
              <Stack
                direction="row"
                gap="7rem"
                alignItems={'center'}
                justifyContent="space-between"
              >
                <Stack direction="row" gap="1rem" flex="1 1 0%" alignItems={'center'}>
                  <Typography variant="h5" whiteSpace={'nowrap'} width={'auto'}>
                    {t('Résumés pré-sélectionnés :')}
                  </Typography>
                  <TextField
                    label={t('Recherche')}
                    variant="outlined"
                    fullWidth
                    value={searchFilter ?? ''}
                    onChange={event => {
                      setSearchFilter(event.target.value)
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Tooltip
                              title={
                                t('Recherche avancée') +
                                ': domain:XXX, theme:XXX, category:XXX, selection_status:XXX, id:XXX, title:XXX'
                              }
                            >
                              <InfoIcon color="primary" />
                            </Tooltip>
                          </InputAdornment>
                        ),
                        endAdornment: searchFilter ? (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setSearchFilter(null)
                              }}
                              edge="end"
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      },
                    }}
                  />
                </Stack>
                <Stack direction="row" flex="1 1 0%">
                  <Autocomplete
                    onChange={(_, value) => {
                      setSelectedSession(value)
                    }}
                    value={selectedSession}
                    options={sessions ?? []}
                    id="sessionid-autocomplete"
                    disablePortal
                    fullWidth
                    disableClearable
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    filterOptions={x => x}
                    getOptionLabel={option => {
                      if (option) {
                        const sameDay = isSameDay(option.start, option.end)
                        return `${option.name} : ${formatHumanDateTime(option.start, true)}-${sameDay ? formatHumanTime(option.end) : formatHumanDateTime(option.end, true)} ${option.tags.length ? `[${option.tags.map(el => tagsDataOptions.find(e => e.value === el)?.label).join(', ')}]` : ''}`
                      }
                      return 'None'
                    }}
                    renderInput={params => (
                      <TextField {...params} label={t('Séance')} variant="outlined" />
                    )}
                  />
                </Stack>
              </Stack>
              <TransferList
                data={dataAbstracts}
                filterData={filterData}
                selected={selected}
                onRight={async selected => {
                  const rejected = [] as number[]
                  await Promise.all(
                    selected.map(async id => {
                      const selectedAbstract = getValues(`abstracts`).find(el => el.id === id)
                      const res = await modifySelectionAbstract({
                        eventId: Number(eventId),
                        id,
                        data: {
                          session: selectedSession.id,
                          presentation_time: selectedAbstract?.presentation_time ?? null,
                          qa_time: selectedAbstract?.qa_time ?? null,
                        },
                      })
                      if ('error' in res) {
                        rejected.push(id)
                      }
                    })
                  )
                  if (rejected.length > 0) {
                    toast.error(t('Erreur lors de la mise à jour de certains résumés') + '.')
                  }
                  revalidating.current = true
                  revalidator.revalidate()
                  await waitForRevalidation()
                  return rejected
                }}
                onLeft={async selected => {
                  const rejected = [] as number[]
                  await Promise.all(
                    selected.map(async id => {
                      const res = await modifySelectionAbstract({
                        eventId: Number(eventId),
                        id,
                        data: { session: null },
                      })
                      if ('error' in res) {
                        rejected.push(id)
                      }
                    })
                  )
                  if (rejected.length > 0) {
                    toast.error(t('Erreur lors de la mise à jour de certains résumés') + '.')
                  }
                  revalidating.current = true
                  revalidator.revalidate()
                  await waitForRevalidation()
                  return rejected
                }}
                selectionGetter={data => data?.id}
                sortGetter={data => data.order ?? 0}
                renderOption={(data, leftOrRight) => (
                  <Stack direction="row" gap="0.5rem">
                    <Stack direction="column" flex="1 1 auto" alignItems="start" gap="0.5rem">
                      <Stack direction="row" gap="0.5rem" alignItems="center">
                        <AbstractSelectionStatusTag size="small" status={data.selection_status} />
                        <Typography variant="body1">
                          {domainsDataOptions.find(el => el.value === data.domain)?.label}
                        </Typography>
                        -
                        {(function renderTheme() {
                          const res = themesDataOptions.find(el => el.value === data.theme)?.label
                          const theme =
                            res?.length !== undefined && res.length > 30
                              ? res.substring(0, 30) + '...'
                              : res
                          return (
                            <Tooltip title={res}>
                              <Typography variant="body1">{theme}</Typography>
                            </Tooltip>
                          )
                        })()}
                      </Stack>
                      {(function renderTitle() {
                        const title = `${data.title.substring(0, 200)}${data.title.length > 200 ? '...' : ''}`
                        return (
                          <Tooltip title={data.title}>
                            <Typography variant="body2">{`(${data.order ?? 0}) [${data.id}] ${title}`}</Typography>
                          </Tooltip>
                        )
                      })()}
                    </Stack>
                    <Stack direction="column" flex="1 1 0" gap="0.5rem" sx={{ minWidth: '7.5rem' }}>
                      <Controller
                        name={`abstracts.${getIndexFromId(data.id)}.presentation_time`}
                        control={control}
                        render={({ field }) => (
                          <TimeField
                            label={t('Présentation')}
                            onClick={e => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                            disabled={leftOrRight === 'right'}
                            readOnly={leftOrRight === 'right'}
                            slotProps={{
                              textField: {
                                inputProps: {
                                  placeholder: defaultAbstractPresentationTime,
                                },
                              },
                            }}
                            defaultValue={
                              field.value
                                ? new Date(`2025-01-01T${field.value}`)
                                : leftOrRight === 'left'
                                  ? null
                                  : new Date(`2025-01-01T${defaultAbstractPresentationTime}`)
                            }
                            onChange={date => {
                              field.onChange(date ? format(date, 'HH:mm:ss') : null)
                            }}
                            format="HH:mm:ss"
                            focused
                            color="info"
                          />
                        )}
                      />
                      <Controller
                        name={`abstracts.${getIndexFromId(data.id)}.qa_time`}
                        control={control}
                        render={({ field }) => (
                          <TimeField
                            label={t('Questions')}
                            disabled={leftOrRight === 'right'}
                            readOnly={leftOrRight === 'right'}
                            slotProps={{
                              textField: {
                                inputProps: {
                                  placeholder: defaultAbstractQaTime,
                                },
                              },
                            }}
                            defaultValue={
                              field.value
                                ? new Date(`2025-01-01T${field.value}`)
                                : leftOrRight === 'left'
                                  ? null
                                  : new Date(`2025-01-01T${defaultAbstractQaTime}`)
                            }
                            onChange={date => {
                              field.onChange(date ? format(date, 'HH:mm:ss') : null)
                            }}
                            format="HH:mm:ss"
                            focused
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                )}
                onDown={async id => {
                  const res = await orderDownSelectionAbstract({
                    eventId: Number(eventId),
                    id,
                  })
                  if ('error' in res) {
                    toast.error(t('Erreur lors du déplacement du résumé') + '.')
                  }
                  revalidating.current = true
                  revalidator.revalidate()
                  await waitForRevalidation()
                }}
                onUp={async id => {
                  const res = await orderUpSelectionAbstract({
                    eventId: Number(eventId),
                    id,
                  })
                  if ('error' in res) {
                    toast.error(t('Erreur lors du déplacement du résumé') + '.')
                  }
                  revalidating.current = true
                  revalidator.revalidate()
                  await waitForRevalidation()
                }}
                excludeData={excludeData}
                setExcludeData={setExcludeData}
                height={`calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 9.5rem${!showStatistics ? ' - 60px' : ''})`}
              />
            </>
          ) : (
            <h2>{t('Aucune séance disponible')}</h2>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
